<template>
  <div id="JtGaugeChart">
    <v-chart ref="chart" class="jtchart" :option="chartSettings" autoresize />
  </div>
</template>

<script>
import { Component, Vue, Watch } from 'vue-property-decorator'
import { dashboardMethods, TEXT_FORMATTING_TYPE } from '../dashboard/shared'

@Component({
  name: 'JtGaugeChart',
  props: {
    data: {
      type: Array,
      required: false,
      default: []
    },
    decimalPlaces: {
      type: Number,
      required: false
    },
    fontSize: {
      type: Number,
      required: false,
      default: 30
    },
    shouldShowAxisTick: {
      type: Boolean,
      required: false,
      default: false
    },
    shouldShowAxisLabel: {
      type: Boolean,
      required: false,
      default: false
    },
    shouldShowPointer: {
      type: Boolean,
      required: false,
      default: false
    },
    shouldShowSplitLine: {
      type: Boolean,
      required: false,
      default: false
    },
    targetValue: {
      type: Number,
      required: true
    },
    textFormattingType: {
      type: Number,
      required: false,
      default: TEXT_FORMATTING_TYPE.DEFAULT
    }
  }
})
export default class JtGaugeChart extends Vue {
  hasMoreThan4Data = false
  hasNoData = false

  gaugeDataNoData = [{
    value: 0,
    name: this.$gettext('NO_DATA'),
    title: {
      offsetCenter: ['0%', '0%']
    },
    detail: {
      show: false
    }
  }]

  chartSettings = {
    series: [
      {
        type: 'gauge',
        radius: '85%',
        startAngle: 90,
        endAngle: -270,
        pointer: {
          show: false
        },
        progress: {
          show: true,
          overlap: false,
          roundCap: true,
          itemStyle: {
            borderWidth: 5,
            borderColor: '#ffffff'
          }
        },
        axisLine: {
          lineStyle: {
            width: 15
          }
        },
        splitLine: {
          show: false
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          show: false,
          distance: 25
        },
        data: this.gaugeDataNoData,
        detail: {
          color: 'inherit',
          fontWeight: 'normal'
        }
      }
    ]
  }

  created () {
    this.chartSettings.series[0].title = {
      fontSize: this.fontSize
    }
    this.chartSettings.series[0].detail.fontSize = this.fontSize
  }

  mounted () {
    setTimeout(() => {
      this.dynamicConfigs()
      this.dataWatcher(this.data)
    }, 1)
  }

  dynamicConfigs () {
    const dynamicSettings = {
      series: [
        {
          max: this.textFormattingType === TEXT_FORMATTING_TYPE.PERCENT ? 100 : this.targetValue,
          pointer: {
            show: this.shouldShowPointer
          },
          splitLine: {
            show: this.shouldShowSplitLine
          },
          axisTick: {
            show: this.shouldShowAxisTick
          },
          axisLabel: {
            show: this.shouldShowAxisLabel,
            formatter: value => {
              if (!value) return ''
              const decimalPlaces = this.decimalPlaces || this.decimalPlaces === 0 ? this.decimalPlaces : this.textFormattingType === TEXT_FORMATTING_TYPE.DEFAULT ? undefined : 2
              return dashboardMethods.getTextFormat(value, decimalPlaces, this.textFormattingType, this.$language.current)
            }
          },
          detail: {
            formatter: value => {
              const decimalPlaces = this.decimalPlaces || this.decimalPlaces === 0 ? this.decimalPlaces : this.textFormattingType === TEXT_FORMATTING_TYPE.DEFAULT ? undefined : 2
              return dashboardMethods.getTextFormat(value, decimalPlaces, this.textFormattingType, this.$language.current)
            }
          }
        }
      ]
    }
    this.$refs.chart.setOption(dynamicSettings)
  }

  formatInformationToGaugeData (information, idx, dataAmount) {
    const informationFormatted = {
      value: this.textFormattingType === TEXT_FORMATTING_TYPE.PERCENT ? (100 * information.value) / this.targetValue : information.value,
      name: information.label || (information.labelConst ? this.$gettext(information.labelConst) : `${this.$gettext('NO_LABEL')}-${idx}`),
      title: {
        offsetCenter: this.getDataTitleOffsetCenter(dataAmount, idx)
      },
      detail: {
        valueAnimation: true,
        offsetCenter: this.getDataDetailOffsetCenter(dataAmount, idx)
      }
    }
    if (information.color) {
      informationFormatted.itemStyle = {
        color: information.color
      }
    }

    return informationFormatted
  }

  getDataTitleOffsetCenter (dataAmount, idx) {
    switch (dataAmount) {
      case 1:
        return ['0%', '5%']
      case 2:
        if (idx === 0) {
          return ['0%', '-10%']
        } else {
          return ['0%', '20%']
        }
      case 3:
        if (idx === 0) {
          return ['0%', '-25%']
        } else if (idx === 1) {
          return ['0%', '5%']
        } else {
          return ['0%', '35%']
        }
      case 4:
        if (idx === 0) {
          return ['-27%', '-10%']
        } else if (idx === 1) {
          return ['27%', '-10%']
        } else if (idx === 2) {
          return ['-27%', '20%']
        } else {
          return ['27%', '20%']
        }
      default:
        break
    }
  }

  getDataDetailOffsetCenter (dataAmount, idx) {
    switch (dataAmount) {
      case 1:
        return ['0%', '-5%']
      case 2:
        if (idx === 0) {
          return ['0%', '-20%']
        } else {
          return ['0%', '10%']
        }
      case 3:
        if (idx === 0) {
          return ['0%', '-35%']
        } else if (idx === 1) {
          return ['0%', '-5%']
        } else {
          return ['0%', '25%']
        }
      case 4:
        if (idx === 0) {
          return ['-27%', '-20%']
        } else if (idx === 1) {
          return ['27%', '-20%']
        } else if (idx === 2) {
          return ['-27%', '10%']
        } else {
          return ['27%', '10%']
        }
      default:
        break
    }
  }

  getGaugeCircleBackGroundColor (data) {
    return data.length === 1 &&
      data[0]?.itemStyle?.color &&
      data[0].itemStyle.color.startsWith('#') ? `${data[0].itemStyle.color}20` : '#E6EBF8'
  }

  getGaugeExceptionData (isNoData) {
    return [{
      value: 0,
      name: isNoData ? this.$gettext('NO_DATA') : this.$gettext('GAUGE_MORE_THAN_4'),
      title: {
        offsetCenter: ['0%', '0%']
      },
      detail: {
        show: false
      }
    }]
  }

  @Watch('data', { deep: true })
  dataWatcher (newData) {
    let newGaugeData
    if (!newData || newData.length === 0 || newData.length > 4) {
      newGaugeData = this.getGaugeExceptionData(!newData || newData.length === 0)
    } else {
      newGaugeData = []
      const dataAmount = newData.length
      newData.forEach((record, idx) => {
        newGaugeData.push(this.formatInformationToGaugeData(record, idx, dataAmount))
      })
    }

    const gaugeCircleLineWidth = 15 * newGaugeData.length

    const dynamicSettings = {
      series: [
        {
          data: newGaugeData,
          axisLine: {
            lineStyle: {
              width: gaugeCircleLineWidth
            }
          },
          axisLabel: {
            show: this.shouldShowAxisLabel,
            distance: gaugeCircleLineWidth + 10
          }
        }
      ]
    }

    dynamicSettings.series[0].axisLine.lineStyle.color = [[1, this.getGaugeCircleBackGroundColor(newGaugeData)]]

    this.$refs.chart.setOption(dynamicSettings)
  }
}

</script>

<style lang='stylus' scoped>
@import '~quasar-variables';

#JtGaugeChart {
  height: 100%;
  width: 100%;
}
</style>
